// Runtime head-tag management for route-aware SEO.
// Keeps exactly one managed tag per name/property/rel, reusing existing tags
// (including any shared fallback tags in index.html) and creating missing ones.

import { SITE_URL, SITE_IMAGE, SITE_NAME } from "./data";

export interface ResolvedSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type: "website" | "article";
}

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setRobots(content: string | null): void {
  const el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (content === null) {
    el?.remove();
    return;
  }
  if (el) {
    el.setAttribute("content", content);
  } else {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", content);
    document.head.appendChild(meta);
  }
}

export function applySeo(seo: ResolvedSeo): void {
  document.title = seo.title;
  upsertMeta("name", "description", seo.description);
  upsertMeta("name", "keywords", seo.keywords);

  const url = SITE_URL + seo.path;
  upsertCanonical(url);

  upsertMeta("property", "og:title", seo.title);
  upsertMeta("property", "og:description", seo.description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", SITE_IMAGE);
  upsertMeta("property", "og:type", seo.type);
  upsertMeta("property", "og:site_name", SITE_NAME);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", seo.title);
  upsertMeta("name", "twitter:description", seo.description);
  upsertMeta("name", "twitter:image", SITE_IMAGE);

  setRobots(null);
}

export function applyNoIndex(title: string): void {
  document.title = title;
  setRobots("noindex, nofollow");
}